import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { allVideos, categories } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'year'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const category = categories.find(c => c.slug === slug);

  useEffect(() => {
    if (slug && !categories.find(c => c.slug === slug)) {
      navigate('/');
    }
  }, [slug, navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [slug, sortBy]);

  const filteredVideos = useMemo(() => {
    let videos = slug === 'all' ? allVideos : allVideos.filter(v => v.categorySlug === slug);
    switch (sortBy) {
      case 'rating':
        return [...videos].sort((a, b) => b.rating - a.rating);
      case 'year':
        return [...videos].sort((a, b) => b.year - a.year);
      default:
        return [...videos].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredVideos.length / pageSize);
  const paginatedVideos = filteredVideos.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const catName = category?.name || '全部';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName} - 91在线视频`,
    description: `91在线视频${catName}频道，汇聚最新${catName}资源，免费在线观看高清不缓冲。`,
    url: `https://www.91onlinevideo.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: paginatedVideos.map((video, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: video.title,
          url: `https://www.91onlinevideo.com/article/${video.id}`,
          image: video.thumbnail,
        },
      })),
    },
  };

  if (!category) return null;

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: catName },
          ]} />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                <i className={`${category.icon} text-primary-500 mr-2`}></i>
                {catName}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共找到 <strong className="text-foreground-800">{filteredVideos.length}</strong> 部{catName}资源
              </p>
            </div>

            <div className="flex items-center gap-2 self-start">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { value: 'latest' as const, label: '最新' },
                { value: 'rating' as const, label: '评分' },
                { value: 'year' as const, label: '年份' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    sortBy === opt.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="mt-6">
            {paginatedVideos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-foreground-400">
                <i className="ri-film-line text-5xl mb-4"></i>
                <p className="text-lg">暂无{catName}相关资源</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {paginatedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group block"
                  >
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200 mb-2.5">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 91在线视频 ${catName}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/75 text-white text-xs px-1.5 py-0.5 rounded">
                        {video.duration}
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded">
                          {video.rating}
                        </span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-10 h-10 bg-primary-500/90 rounded-full flex items-center justify-center">
                          <i className="ri-play-fill text-white text-lg ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500 flex-wrap">
                      <span>{video.type}</span>
                      <span>·</span>
                      <span>{video.year}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <nav className="flex items-center justify-center gap-1 py-10" aria-label="分页导航">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                    currentPage === page
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </nav>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}